import React, { useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '@mui/material';

export interface BaseChartProps {
  option: any;
  height?: number | string;
}

export const BaseChart: React.FC<BaseChartProps> = ({ option, height = '100%' }) => {
  const theme = useTheme();
  const chartRef = useRef<ReactECharts>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      const instance = chartRef.current?.getEchartsInstance();
      if (instance) instance.resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Merge theme-consistent defaults into the custom option object
  const mergedOption = {
    ...option,
    title: option.title ? {
      ...option.title,
      textStyle: {
        fontSize: 16,
        fontWeight: 500,
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        ...option.title.textStyle,
      }
    } : undefined,
    tooltip: {
      trigger: 'axis',
      backgroundColor: theme.palette.background.paper,
      textStyle: { 
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
        fontSize: 12,
      },
      borderWidth: 1,
      borderColor: theme.palette.divider,
      shadowColor: 'transparent',
      ...option.tooltip,
    },
    legend: option.legend ? {
      bottom: 0,
      textStyle: { 
        color: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
      },
      ...option.legend,
    } : undefined,
    grid: {
      left: '3%',
      right: '4%',
      bottom: option.legend ? '15%' : '10%',
      containLabel: true,
      ...option.grid,
    },
    xAxis: option.xAxis ? (Array.isArray(option.xAxis) ? option.xAxis : [option.xAxis]).map((x: any) => ({
      axisLine: { lineStyle: { color: theme.palette.divider } },
      axisLabel: { 
        color: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
      },
      ...x,
    })) : undefined,
    yAxis: option.yAxis ? (Array.isArray(option.yAxis) ? option.yAxis : [option.yAxis]).map((y: any) => ({
      axisLabel: { 
        color: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
      },
      nameTextStyle: { 
        color: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
      },
      splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      ...y,
    })) : undefined,
  };

  // Convert array-type axis options back to single objects if the input was single
  if (mergedOption.xAxis && !Array.isArray(option.xAxis)) {
    mergedOption.xAxis = mergedOption.xAxis[0];
  }
  if (mergedOption.yAxis && !Array.isArray(option.yAxis)) {
    mergedOption.yAxis = mergedOption.yAxis[0];
  }

  return (
    <div ref={containerRef} style={{ height, width: '100%' }}>
      <ReactECharts
        ref={chartRef}
        option={mergedOption}
        style={{ height: '100%', width: '100%' }}
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};
